import React, { FormEvent, useState } from 'react';
import { Body, H1, Label } from '../ui/Typography';
import Button from '../ui/Button';
import { ContactFormValues, validateContactForm } from '../../utils/contact';

const initialValues: ContactFormValues = {
  name: '',
  email: '',
  message: '',
};

export default function ContactPage() {
  const [values, setValues] = useState<ContactFormValues>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormValues, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusTone, setStatusTone] = useState<'idle' | 'success' | 'error'>('idle');

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validateContactForm(values);
    setErrors(nextErrors);
    setStatusMessage('');
    setStatusTone('idle');

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      const payload = await response.json();

      if (!response.ok || !payload.success) {
        throw new Error(payload.error || 'Something went wrong.');
      }

      setValues(initialValues);
      setErrors({});
      setStatusTone('success');
      setStatusMessage('Message sent successfully. You should hear back soon.');
    } catch (error) {
      setStatusTone('error');
      setStatusMessage(error instanceof Error ? error.message : 'Something went wrong.');
    } finally {
      setIsSubmitting(false);
    }
  }

  function updateField(field: keyof ContactFormValues, value: string) {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  }

  return (
    <div className="portfolio-shell pb-24">
      <section className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="space-y-5">
          <Label className="text-neonOrange">Contact</Label>
          <H1 className="max-w-4xl text-[clamp(3rem,8vw,6rem)] leading-[0.92]">For launches, refreshes, and visual systems that need to feel live on day one.</H1>
          <Body className="max-w-xl text-lg text-text-secondary">
            Share the brief, the timing, or the mess. This route is wired to the existing site contact backend, so inquiries land in the same system already used on the site.
          </Body>
          <div className="space-y-2 border border-border-subtle bg-surface-elevated/70 p-5 text-sm uppercase tracking-[0.16em] text-text-secondary backdrop-blur-sm">
            <p>Email: johnmooredesigncloud@gmail.com</p>
            <p>Focus: hospitality, campaign systems, launch art direction</p>
            <p>Remote collaborations / location-flexible engagements</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 border border-border-subtle bg-surface-card/80 p-6 backdrop-blur-sm" noValidate>
          <div className="space-y-2">
            <label htmlFor="name" className="text-xs uppercase tracking-[0.18em] text-text-secondary">
              Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={values.name}
              onChange={(event) => updateField('name', event.target.value)}
              className="w-full border border-border-subtle bg-surface-input px-4 py-3 text-base text-text-primary placeholder:text-text-muted"
              placeholder="Your name"
              aria-invalid={Boolean(errors.name)}
              aria-describedby={errors.name ? 'name-error' : undefined}
            />
            {errors.name ? (
              <p id="name-error" className="text-sm text-[#ff8a65]" aria-live="polite">
                {errors.name}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-text-secondary">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={values.email}
              onChange={(event) => updateField('email', event.target.value)}
              className="w-full border border-border-subtle bg-surface-input px-4 py-3 text-base text-text-primary placeholder:text-text-muted"
              placeholder="name@company.com"
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            {errors.email ? (
              <p id="email-error" className="text-sm text-[#ff8a65]" aria-live="polite">
                {errors.email}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <label htmlFor="message" className="text-xs uppercase tracking-[0.18em] text-text-secondary">
              Brief
            </label>
            <textarea
              id="message"
              name="message"
              rows={8}
              value={values.message}
              onChange={(event) => updateField('message', event.target.value)}
              className="w-full border border-border-subtle bg-surface-input px-4 py-3 text-base text-text-primary placeholder:text-text-muted"
              placeholder="What are you launching, refreshing, or fixing?"
              aria-invalid={Boolean(errors.message)}
              aria-describedby={errors.message ? 'message-error' : undefined}
            />
            {errors.message ? (
              <p id="message-error" className="text-sm text-[#ff8a65]" aria-live="polite">
                {errors.message}
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <Button
              type="submit"
              loading={isSubmitting}
              className="px-6 py-3 text-sm font-mono uppercase tracking-[0.18em]"
            >
              Send inquiry
            </Button>
            {statusMessage ? (
              <p
                className={statusTone === 'success' ? 'text-sm text-[#cfecc6]' : 'text-sm text-[#ff8a65]'}
                aria-live="polite"
              >
                {statusMessage}
              </p>
            ) : null}
          </div>
        </form>
      </section>
    </div>
  );
}
